import { trpc } from "@client/trpc";
import { useCallback } from "react";
import { useImpersonationRefValue } from "@client/impersonationRef";
import { todoChecklistQueryInput } from "./todoQueryInput";
import { useTodoRailSync } from "./TodoRailSyncProvider";

/** Writes one checklist row — an observation, or a manual mark. */
export function useSetChecklistMark(
  roundId: string,
): (itemKey: string, done: boolean) => void {
  const viewAsUserId = useImpersonationRefValue();
  const utils = trpc.useUtils();
  // Same key the rail reads — see todoChecklistQueryInput.
  const queryInput = todoChecklistQueryInput(roundId, viewAsUserId);

  // Queued through the durable outbox — see TodoRailSyncProvider.
  const sync = useTodoRailSync();

  return useCallback(
    (itemKey: string, done: boolean) => {
      // No outbox means no durable write, so don't paint the optimistic state
      // either — a tick the server will never hear about is worse than a
      // control that visibly did nothing. `enqueueMark` is also absent while
      // viewing as someone else: that progress is theirs, not ours to move.
      if (sync?.enqueueMark == null) {
        return;
      }

      const enqueueMark = sync.enqueueMark;
      const clientUpdatedAt = new Date().toISOString();

      utils.getRoundChecklist.setData(queryInput, (prev) => {
        if (prev == null) {
          return prev;
        }

        const rest = prev.marks.filter((mark) => mark.itemKey !== itemKey);
        return {
          ...prev,
          marks: [...rest, { itemKey, done, clientUpdatedAt }],
        };
      });

      enqueueMark({ roundId, itemKey, done, clientUpdatedAt });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queryInput is rebuilt each render; its parts are the real deps.
    [roundId, viewAsUserId, sync, utils],
  );
}
